/**
 * Central utility functions for issue management
 */

import { buildNetlifyCallback } from '@/lib/backend';
import { DatabaseSelectRequest } from '@/types/database';

const doDatabaseSelect = buildNetlifyCallback<DatabaseSelectRequest<any>, any[]>("DatabaseSelect");

/**
 * Generate the next issue number for a new issue
 * @returns Promise<string> The next issue number in format "ISS-XXX"
 */
export async function generateNextIssueNumber(): Promise<string> {
  const existingIssuesResult = await doDatabaseSelect({
    table: 'issues',
    orderColumn: 'created_at',
    orderOptions: { ascending: false },
    limit: 1,
  }) as { data?: any[]; error?: any };

  const existingIssues = existingIssuesResult.data || [];

  const nextNumber = existingIssues && existingIssues.length > 0
    ? parseInt(existingIssues[0].issue_number.split('-')[1]) + 1
    : 1;
  
  return `ISS-${String(nextNumber).padStart(3, '0')}`;
}

/**
 * Get the URL path for an issue
 * Prefers issue_number if available, falls back to issue.id
 * @param issueIdOrNumber Either issue ID (UUID) or issue number (ISS-XXX)
 * @returns string The URL path for the issue
 */
export function getIssueUrl(issueIdOrNumber: string): string {
  return `/issue/${issueIdOrNumber}`;
}

/**
 * Navigate to an issue using react-router's navigate function
 * @param navigate The navigate function from useNavigate()
 * @param issueIdOrNumber Either issue ID (UUID) or issue number (ISS-XXX)
 */
export function navigateToIssue(navigate: (path: string) => void, issueIdOrNumber: string): void {
  navigate(getIssueUrl(issueIdOrNumber));
}